import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  FileText, 
  Clock, 
  User, 
  Stethoscope, 
  Pill, 
  Calendar,
  Download,
  Eye,
  EyeOff,
  Star
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

interface TranscriptData {
  id: string;
  booking_id: string;
  practitioner_id: string;
  client_id: string;
  transcript_text: string;
  ai_analysis: any;
  recording_duration_seconds: number;
  created_at: string;
  updated_at: string;
  status: string;
  practitioner?: {
    first_name: string;
    last_name: string;
    professional_title?: string;
  };
  client?: {
    first_name: string;
    last_name: string;
  };
  booking?: {
    service: {
      service_name: string;
    };
  };
}

interface TranscriptViewerProps {
  bookingId: string;
  onClose?: () => void;
}

export const TranscriptViewer: React.FC<TranscriptViewerProps> = ({
  bookingId,
  onClose
}) => {
  const { toast } = useToast();
  const { t } = useTranslation('dashboard');
  const [transcripts, setTranscripts] = useState<TranscriptData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTranscript, setSelectedTranscript] = useState<TranscriptData | null>(null);
  const [showFullTranscript, setShowFullTranscript] = useState(false);

  // Fetch transcripts for the booking
  useEffect(() => {
    const fetchTranscripts = async () => {
      try {
        const { data, error } = await supabase
          .from('session_transcripts')
          .select(`
            *,
            practitioner:practitioners(first_name, last_name, professional_title),
            client:clients(first_name, last_name),
            booking:bookings(
              service:services(service_name)
            )
          `)
          .eq('booking_id', bookingId)
          .order('created_at', { ascending: false });

        if (error) throw error;
        
        setTranscripts(data || []);
        if (data && data.length > 0) {
          setSelectedTranscript(data[0]);
        }
      } catch (error) {
        console.error('Error fetching transcripts:', error);
        toast({
          title: t('transcripts.fetchFailed'),
          description: t('transcripts.failedToLoad'),
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTranscripts();
  }, [bookingId, toast]);

  // Format duration
  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Generate session report
  const generateSessionReport = (transcript: TranscriptData): string => {
    const analysis = transcript.ai_analysis;
    if (!analysis) return '';

    const practitionerName = transcript.practitioner ? 
      `${transcript.practitioner.first_name} ${transcript.practitioner.last_name}` : 
      '未知医生';
    
    const clientName = transcript.client ? 
      `${transcript.client.first_name} ${transcript.client.last_name}` : 
      '患者';

    const serviceName = transcript.booking?.service?.service_name || '诊疗服务';

    let report = `# ${t('transcripts.medicalRecord')}\n\n`;
    report += `**${t('transcripts.date')}**: ${format(new Date(transcript.created_at), 'yyyy年MM月dd日')}\n`;
    report += `**${t('transcripts.doctor')}**: ${practitionerName}\n`;
    report += `**${t('transcripts.patient')}**: ${clientName}\n`;
    report += `**${t('transcripts.service')}**: ${serviceName}\n`;
    report += `**${t('transcripts.recordingDuration')}**: ${formatDuration(transcript.recording_duration_seconds)}\n\n`;

    if (analysis.summary) {
      report += `## ${t('transcripts.sessionSummary')}\n${analysis.summary}\n\n`;
    }

    if (analysis.symptoms && analysis.symptoms.length > 0) {
      report += `## ${t('transcripts.symptoms')}\n`;
      analysis.symptoms.forEach((symptom: string, index: number) => {
        report += `${index + 1}. ${symptom}\n`;
      });
      report += '\n';
    }

    if (analysis.diagnosis) {
      report += `## ${t('transcripts.diagnosis')}\n${analysis.diagnosis}\n\n`;
    }

    if (analysis.treatment_plan) {
      report += `## ${t('transcripts.treatmentPlan')}\n${analysis.treatment_plan}\n\n`;
    }

    if (analysis.prescriptions && analysis.prescriptions.length > 0) {
      report += `## ${t('transcripts.prescriptions')}\n`;
      analysis.prescriptions.forEach((prescription: any, index: number) => {
        report += `${index + 1}. **${prescription.medication}**\n`;
        if (prescription.dosage) report += `   - ${t('transcripts.dosage')}: ${prescription.dosage}\n`;
        if (prescription.duration) report += `   - ${t('transcripts.duration')}: ${prescription.duration}\n`;
      });
      report += '\n';
    }

    if (analysis.follow_up) {
      report += `## ${t('transcripts.followUp')}\n${analysis.follow_up}\n\n`;
    }

    if (analysis.key_points && analysis.key_points.length > 0) {
      report += `## ${t('transcripts.keyPoints')}\n`;
      analysis.key_points.forEach((point: string, index: number) => {
        report += `${index + 1}. ${point}\n`;
      });
      report += '\n';
    }

    if (analysis.recommendations && analysis.recommendations.length > 0) {
      report += `## ${t('transcripts.recommendations')}\n`;
      analysis.recommendations.forEach((recommendation: string, index: number) => {
        report += `${index + 1}. ${recommendation}\n`;
      });
      report += '\n';
    }

    if (analysis.confidence_score) {
      report += `## ${t('transcripts.analysisConfidence')}\n${Math.round(analysis.confidence_score * 100)}%\n\n`;
    }

    return report;
  };

  // Download report
  const downloadReport = () => {
    if (!selectedTranscript) return;

    const report = generateSessionReport(selectedTranscript);
    const blob = new Blob([report], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${t('transcripts.medicalRecord')}_${format(new Date(selectedTranscript.created_at), 'yyyy-MM-dd')}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Card className="w-full max-w-6xl mx-auto">
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-2">{t('transcripts.loading')}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (transcripts.length === 0) {
    return (
      <Card className="w-full max-w-6xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {t('transcripts.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-8">
            {t('transcripts.noRecords')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-6xl mx-auto">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {t('transcripts.title')} ({transcripts.length})
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              onClick={downloadReport}
              disabled={!selectedTranscript}
              variant="outline"
              className="flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              {t('transcripts.downloadReport')}
            </Button>
            {onClose && (
              <Button onClick={onClose} variant="outline">
                {t('transcripts.close')}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Transcript List */}
        <div className="space-y-2">
          <h3 className="font-medium">{t('transcripts.recordsList')}</h3>
          <ScrollArea className="h-80">
            {transcripts.map((transcript) => (
              <Card 
                key={transcript.id}
                className={`cursor-pointer transition-colors ${
                  selectedTranscript?.id === transcript.id ? 'ring-2 ring-primary' : ''
                }`}
                onClick={() => setSelectedTranscript(transcript)}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">
                        {formatDuration(transcript.recording_duration_seconds)}
                      </span>
                    </div>
                    <Badge variant={transcript.status === 'completed' ? 'default' : 'secondary'}>
                      {transcript.status === 'completed' ? t('transcripts.completed') : transcript.status}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {format(new Date(transcript.created_at), 'MM/dd HH:mm')}
                  </div>
                </CardContent>
              </Card>
            ))}
          </ScrollArea>
        </div>

        {/* Transcript Details */}
        <div className="md:col-span-2">
          {selectedTranscript ? (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium">{t('transcripts.recordDetails')}</h3>
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => setShowFullTranscript(!showFullTranscript)}
                    variant="outline"
                    size="sm"
                  >
                    {showFullTranscript ? (
                      <>
                        <EyeOff className="h-4 w-4 mr-1" />
                        {t('transcripts.hideTranscript')}
                      </>
                    ) : (
                      <>
                        <Eye className="h-4 w-4 mr-1" />
                        {t('transcripts.showTranscript')}
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">{t('transcripts.doctor')}:</span>
                  <span className="ml-2">
                    {selectedTranscript.practitioner ? 
                      `${selectedTranscript.practitioner.first_name} ${selectedTranscript.practitioner.last_name}` : 
                      '未知'
                    }
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">{t('transcripts.recordingDuration')}:</span>
                  <span className="ml-2">{formatDuration(selectedTranscript.recording_duration_seconds)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">{t('transcripts.createdAt')}:</span>
                  <span className="ml-2">{format(new Date(selectedTranscript.created_at), 'yyyy-MM-dd HH:mm')}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">{t('transcripts.status')}:</span>
                  <span className="ml-2">
                    <Badge variant={selectedTranscript.status === 'completed' ? 'default' : 'secondary'}>
                      {selectedTranscript.status === 'completed' ? t('transcripts.completed') : selectedTranscript.status}
                    </Badge>
                  </span>
                </div>
              </div>

              <Separator />

              {/* AI Analysis */}
              {selectedTranscript.ai_analysis && (
                <ScrollArea className="h-64">
                  <div className="space-y-3">
                    {selectedTranscript.ai_analysis.summary && (
                      <div>
                        <h4 className="font-medium flex items-center gap-2">
                          <Stethoscope className="h-4 w-4" />
                          {t('transcripts.sessionSummary')}
                        </h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          {selectedTranscript.ai_analysis.summary}
                        </p>
                      </div>
                    )}

                    {selectedTranscript.ai_analysis.symptoms && selectedTranscript.ai_analysis.symptoms.length > 0 && (
                      <div>
                        <h4 className="font-medium">{t('transcripts.symptoms')}</h4>
                        <ul className="text-sm text-muted-foreground mt-1 list-disc pl-5">
                          {selectedTranscript.ai_analysis.symptoms.map((symptom: string, index: number) => (
                            <li key={index}>{symptom}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {selectedTranscript.ai_analysis.diagnosis && (
                      <div>
                        <h4 className="font-medium">{t('transcripts.diagnosis')}</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          {selectedTranscript.ai_analysis.diagnosis}
                        </p>
                      </div>
                    )}

                    {selectedTranscript.ai_analysis.prescriptions && selectedTranscript.ai_analysis.prescriptions.length > 0 && (
                      <div>
                        <h4 className="font-medium flex items-center gap-2">
                          <Pill className="h-4 w-4" />
                          {t('transcripts.prescription')}
                        </h4>
                        <div className="text-sm text-muted-foreground mt-1 space-y-1">
                          {selectedTranscript.ai_analysis.prescriptions.map((prescription: any, index: number) => (
                            <div key={index} className="border-l-2 border-primary/20 pl-3">
                              <div className="font-medium">{prescription.medication}</div>
                              {prescription.dosage && <div>{t('transcripts.dosage')}: {prescription.dosage}</div>}
                              {prescription.duration && <div>{t('transcripts.duration')}: {prescription.duration}</div>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {selectedTranscript.ai_analysis.confidence_score && (
                      <div>
                        <h4 className="font-medium flex items-center gap-2">
                          <Star className="h-4 w-4" />
                          {t('transcripts.analysisConfidence')}
                        </h4>
                        <div className="text-sm text-muted-foreground mt-1">
                          {Math.round(selectedTranscript.ai_analysis.confidence_score * 100)}%
                        </div>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              )}

              {/* Full Transcript */}
              {showFullTranscript && (
                <>
                  <Separator />
                  <div>
                    <h4 className="font-medium">{t('transcripts.fullTranscript')}</h4>
                    <ScrollArea className="h-32 mt-2">
                      <div className="text-sm text-muted-foreground p-3 bg-muted/20 rounded">
                        {selectedTranscript.transcript_text}
                      </div>
                    </ScrollArea>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="text-center text-muted-foreground py-8">
              {t('transcripts.selectRecord')}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};